import React, { useEffect, useState, useRef } from 'react';
import {
  Nav,
  NavLink,
  NavbarContainer,
  Span,
  NavLogo,
  NavItems,
  GitHubButton,
  ButtonContainer,
  MobileIcon,
  MobileMenu,
  MobileLink,
  NavItemsWrapper,
  ThemeToggleButton,
  MobileThemeToggleButton,
  StarButton,
} from './NavbarStyledComponent';

import { DiCssdeck } from 'react-icons/di';
import { FaBars, FaHome, FaProjectDiagram, FaArrowUp, FaSun, FaMoon, FaStar } from 'react-icons/fa';
import { Bio } from '../../data/constants';
import { useTheme } from 'styled-components';
import GlassEffectNotify from '../Notification/GlassEffectNotify';
import { Link as ScrollLink } from 'react-scroll';
import styled from 'styled-components';

// Bottom navbar (mobile only)
const BottomNav = styled.div`
  position: fixed;
  bottom: ${({ show }) => (show ? '0' : '-80px')};
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.bg + 'ee'};
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 60px;
  z-index: 1000;
  transition: bottom 0.4s ease-in-out;

  @media (min-width: 769px) {
    display: none;
  }
`;

const BottomNavButton = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-size: 1.3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const BottomThemeToggle = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.text_primary};
  font-size: 1.4rem;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.primary};
  }
`;

const Navbar = ({ toggleTheme, toggleStar, isStarThemeActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [shrink, setShrink] = useState(false);
  const [showBottomNav, setShowBottomNav] = useState(true);
  const [showNavbar, setShowNavbar] = useState(true);
  const theme = useTheme();
  const isDarkMode = theme.starEffect || theme.bg === "#1C1C27";

  // Ref for the mobile menu container to detect outside clicks
  const menuRef = useRef(null);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Shrink effect for top navbar
      setShrink(currentScrollY > 50);

      // Hide/show top navbar on scroll direction
      if (currentScrollY > lastScrollY && currentScrollY > 60) {
        setShowNavbar(false); // scrolling down
      } else {
        setShowNavbar(true); // scrolling up
      }

      // Hide/show bottom navbar for mobile
      if (currentScrollY < 50) {
        setShowBottomNav(true);
      } else if (currentScrollY > lastScrollY) {
        setShowBottomNav(false);
      } else {
        setShowBottomNav(true);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleStarClick = () => {
    if (typeof toggleStar === 'function') {
      toggleStar();
    } else {
      console.warn('toggleStar is not a function');
    }
  };

  return (
    <>
      <GlassEffectNotify />
      <Nav
        shrink={shrink}
        isStarTheme={isStarThemeActive}
        style={{
          transform: showNavbar ? 'translateY(0)' : 'translateY(-100%)',
          transition: 'transform 0.3s ease',
        }}
      >
        <NavbarContainer>
          <NavLogo to="/" shrink={shrink}>
            <DiCssdeck size="2rem" />
            <Span shrink={shrink}>Portfolio</Span>
          </NavLogo>

          <MobileIcon>
            <FaBars onClick={() => setIsOpen(!isOpen)} />
          </MobileIcon>

          <NavItemsWrapper>
            <NavItems>
              <ScrollLink to="about" smooth duration={600} offset={-60}>
                <NavLink as="div">About</NavLink>
              </ScrollLink>
              <ScrollLink to="skills" smooth duration={600} offset={-60}>
                <NavLink as="div">Skills</NavLink>
              </ScrollLink>
              <ScrollLink to="experience" smooth duration={600} offset={-60}>
                <NavLink as="div">Experience</NavLink>
              </ScrollLink>
              <ScrollLink to="projects" smooth duration={600} offset={-60}>
                <NavLink as="div">Projects</NavLink>
              </ScrollLink>
              <ScrollLink to="education" smooth duration={600} offset={-60}>
                <NavLink as="div">Education</NavLink>
              </ScrollLink>
            </NavItems>
          </NavItemsWrapper>

          <ButtonContainer>
            <GitHubButton
              shrink={shrink}
              href={Bio.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              Github
            </GitHubButton>
            <ThemeToggleButton onClick={toggleTheme}>Theme</ThemeToggleButton>
            <StarButton onClick={handleStarClick}>
              <FaStar color={isStarThemeActive ? 'gold' : 'inherit'} />
            </StarButton>
          </ButtonContainer>

          {isOpen && (
            <MobileMenu ref={menuRef} isOpen={isOpen}>
              <MobileLink href="#about" onClick={() => setIsOpen(false)}>
                About
              </MobileLink>
              <MobileLink href="#skills" onClick={() => setIsOpen(false)}>
                Skills
              </MobileLink>
              <MobileLink href="#experience" onClick={() => setIsOpen(false)}>
                Experience
              </MobileLink>
              <MobileLink href="#projects" onClick={() => setIsOpen(false)}>
                Projects
              </MobileLink>
              <MobileLink href="#education" onClick={() => setIsOpen(false)}>
                Education
              </MobileLink>

              <GitHubButton
                style={{
                  padding: '10px 16px',
                  background: theme.card_light + '99',
                  color: theme.primary,
                  backdropFilter: 'blur(10px)',
                  border: `1.5px solid ${theme.primary}`,
                }}
                href={Bio.github}
                target="_blank"
                rel="noopener noreferrer"
              >
                Github Profile
              </GitHubButton>

              <MobileThemeToggleButton
                onClick={() => {
                  toggleTheme();
                  setIsOpen(false);
                }}
              >
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </MobileThemeToggleButton>
            </MobileMenu>
          )}
        </NavbarContainer>
      </Nav>

      {/* Bottom Navbar (Mobile Only) */}
      <BottomNav show={showBottomNav}>
        <BottomNavButton href="#about">
          <FaHome />
        </BottomNavButton>
        <BottomNavButton href="#projects">
          <FaProjectDiagram />
        </BottomNavButton>
        <BottomThemeToggle onClick={toggleTheme}>
          {isDarkMode ? <FaSun /> : <FaMoon />}
        </BottomThemeToggle>
        <BottomNavButton href="#">
          <FaArrowUp
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />
        </BottomNavButton>
      </BottomNav>
    </>
  );
};

export default Navbar;
